import { Component, OnInit } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  EstimationMethod,
  MeteredDataService,
  MeterStandType,
  ObservationTimeSeriesPeriod,
  ProductCharacteristicUnitType,
  QuantityQuality,
  SubmitExternalProfileMeteredDataCommand
} from '@app/api/dmc';
import { FormApiService } from '@app/core/services/form-api.service';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { AlertComponent } from '@shared/components/alert/alert.component';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonDirective } from '@shared/components/input/button/button.directive';
import { getDaysBetween } from '@app/core/utils/get-days-between';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { AlertService } from '@app/core/services/alert.service';
import { dateRangeValidator } from '@shared/validators/date-range.validator';
import { InputDateDirective } from '@shared/components/input/date/input-date.directive';
import { MINIMUM_YEAR } from '@app/core/constants/minimum-year';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';

@Component({
  selector: 'app-submit-timeseries-modal',
  imports: [
    ReactiveFormsModule,
    AlertComponent,
    InputGroupComponent,
    LabelDirective,
    InputDirective,
    SelectDirective,
    TranslatePipe,
    ButtonDirective,
    InputDateDirective,
    DecimalPipe,
  ],
  templateUrl: './submit-timeseries-modal.component.html',
  styleUrl: './submit-timeseries-modal.component.css'
})
export class SubmitTimeseriesModalComponent implements OnInit, HasUnsavedChanges {
  // SubmitManualMeteredDataDto
  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public form: FormGroup = new FormGroup({
    meteringPointIdentification: new FormControl(null, Validators.required),
    unitType: new FormControl(ProductCharacteristicUnitType.KWH, Validators.required),
    resolution: new FormControl(ObservationTimeSeriesPeriod.Hour, Validators.required),
    quantityQuality: new FormControl(null, Validators.required),
    wantsDataLocked: new FormControl(false),
    estimationMethod: new FormControl(null),
    meteredData: new FormControl([], Validators.required),
    otherMeteringPoint: new FormControl({ disabled: true, value: null }, Validators.required),
  });

  /** Value form **/
  public valueForm: FormGroup = new FormGroup({
    start: new FormControl(null, [Validators.required]),
    end: new FormControl(null, [Validators.required]),
    periodUsage: new FormControl(0, [Validators.required]),
    estimationMethod: new FormControl(null, [Validators.required]),
  });

  /** Interval Form - template for a single day **/
  public intervalForm: FormGroup = new FormGroup({});

  /** Interval Array - only for one day (24 hours or 96 quarters) **/
  public intervalArray: number[][] = [];

  /** Column Array **/
  public columns: string[] = [];

  /** Total days in range **/
  public totalDays: number = 0;

  /** Loading state for estimation **/
  public isEstimating: boolean = false;

  /** Resolution from MeteringPoint **/
  public resolution?: ObservationTimeSeriesPeriod;

  /** Dropdown values **/
  public resolutionList: KeyValue[] = enumToKeyValue(ObservationTimeSeriesPeriod, { omitKeys: ['NotSpecified', 'Year', 'Month', 'Day', 'Other'], prefixValue: 'common.' });
  public unitType: KeyValue[] = enumToKeyValue(ProductCharacteristicUnitType, { omitKeys: ['NotSpecified',  'KW', 'MVAr', 'MW', 'MWh', 'Tonne', 'Ampere', 'Stk'], prefixValue: 'enum.meteringpoint.unittype.' });
  public quantityQuality: KeyValue[] = enumToKeyValue(QuantityQuality, { omitKeys: ['NotSpecified', 'Revised', 'Calculated'], prefixValue: 'meteringpoint.timeseries.' });
  public estimationMethods: KeyValue[] = enumToKeyValue(EstimationMethod, { includeKeys: [
      EstimationMethod.ManualEvenly,
      EstimationMethod.ManualLastPosition,
      EstimationMethod.ManualProfile,
      EstimationMethod.ManualOtherMeteringPoint
    ], prefixValue: 'enum.estimationmethod.' });

  /** ID **/
  public id?: string;

  constructor(
    private readonly meteredDataService: MeteredDataService,
    private readonly formApiService: FormApiService,
    private readonly alertService: AlertService
  ) {
    if (this.id) {
      this.form.get('meteringPointIdentification')?.setValue(this.id);
    }

    // Remove the 10-day limit - users can now submit any number of days
    this.valueForm.get('start')?.addValidators(dateRangeValidator('start', 'end', 1, 36500));
    this.valueForm.get('end')?.addValidators(dateRangeValidator('start', 'end', 1, 36500));

    this.valueForm.get('start')?.valueChanges.subscribe((date: any) => {
      const d = new Date(date);
      if (d && d.getFullYear() >= MINIMUM_YEAR) {
        this.valueForm.get('end')?.updateValueAndValidity({ emitEvent: false });
        this.updateTableSize();
      }
    })

    this.valueForm.get('end')?.valueChanges.subscribe((date: any) => {
      const d = new Date(date);
      if (d && d.getFullYear() >= MINIMUM_YEAR)  {
        this.valueForm.get('start')?.updateValueAndValidity({ emitEvent: false });
        this.updateTableSize();
      }
    })

    this.form.get('resolution')?.valueChanges.subscribe(() => {
      this.updateTableSize();
    });

    this.valueForm.get('estimationMethod')?.valueChanges.subscribe((value: any) => {
      // Reset interval fields when estimation method changes
      this.resetIntervalFields();
      this.alertService.clearAll();

      this.form.get('otherMeteringPoint')?.disable();
      switch (value) {
        case EstimationMethod.ManualEvenly:
        case EstimationMethod.ManualLastPosition:
          this.form.get('resolution')?.setValidators(Validators.required);
          this.form.get('unitType')?.setValidators(Validators.required);
          this.form.get('quantityQuality')?.enable();
          break;
        case EstimationMethod.ManualProfile:
          this.form.get('quantityQuality')?.disable();
          break;
        case EstimationMethod.ManualOtherMeteringPoint:
          this.form.get('otherMeteringPoint')?.enable();
          this.form.get('quantityQuality')?.disable();
          break;
      }
    });
  }

  public ngOnInit(): void {
    if (this.resolution) {
      this.form.get('resolution')?.setValue(this.resolution);
    }
  }

  /**
   * Reset all interval fields to 0
   */
  private resetIntervalFields(): void {
    const resetValues: { [key: string]: number } = {};

    for (const array of this.intervalArray) {
      for (const element of array) {
        resetValues[this.formatFormControlName(element)] = 0;
      }
    }

    this.intervalForm.patchValue(resetValues);
  }

  public shouldShowElement(field: string): boolean {
    let estimationMethod = this.valueForm.get('estimationMethod')?.value;

    switch (field) {
      case 'otherMeteringPoint':
        return ['ManualOtherMeteringPoint'].includes(estimationMethod);
      case 'quantityQuality':
        return ['ManualEvenly', 'ManualLastPosition'].includes(estimationMethod);
      case 'unitType':
        return ['ManualEvenly', 'ManualLastPosition'].includes(estimationMethod);
      case 'resolution':
        return ['ManualEvenly', 'ManualLastPosition'].includes(estimationMethod);
      case 'wantsDataLocked':
        return ['ManualEvenly', 'ManualLastPosition', 'ManualProfile', 'ManualOtherMeteringPoint'].includes(estimationMethod);
      case 'distributeValues':
        return ['ManualEvenly', 'ManualLastPosition'].includes(estimationMethod);
      case 'intervalFields':
        // Hide interval fields for ManualProfile and ManualOtherMeteringPoint
        return !['ManualProfile', 'ManualOtherMeteringPoint'].includes(estimationMethod);
      default:
        return false;
    }
  }

  public updateTableSize(): void {
    const startValue = this.valueForm.get('start')?.value;
    const endValue = this.valueForm.get('end')?.value;
    const resolutionValue = this.form.get('resolution')?.value;

    this.totalDays = getDaysBetween(startValue, endValue);

    this.intervalForm = new FormGroup({});
    this.intervalArray = [];
    this.columns = [];

    if (startValue === null || endValue === null || resolutionValue === null) {
      return;
    }

    // Check for errors
    if (this.valueForm.get('start')?.invalid || this.valueForm.get('end')?.invalid) {
      return;
    }

    // Only create controls for ONE day (template)
    if (resolutionValue === 'Hour') {
      this.updateTableSizeToHour();
    }
    else if (resolutionValue === 'Quarter')
    {
      this.updateTableSizeToQuarter();
    }
  }

  public updateTableSizeToHour(): void {
    // Show 24 rows for ONE day (template)
    this.columns = ["Værdi"];
    this.intervalArray = new Array(24).fill(0).map((_, i) => [i]);

    for (const hourArray of this.intervalArray) {
      for (const hour of hourArray) {
        this.intervalForm.addControl(this.formatFormControlName(hour), new FormControl(0, Validators.required));
      }
    }
  }

  public updateTableSizeToQuarter(): void {
    // Show 96 rows for ONE day (template)
    this.columns = ["00-15", "15-30", "30-45", "45-00"];
    this.intervalArray = new Array(24).fill(0).map((_, i) => [i, i + 0.25, i + 0.5, i + 0.75]);

    for (const quarterArray of this.intervalArray) {
      for (const quarter of quarterArray) {
        this.intervalForm.addControl(this.formatFormControlName(quarter), new FormControl(0, Validators.required));
      }
    }
  }

  public formatFormControlName(value: number): string {
    return `interval_${value.toFixed(2).replace('.', '_')}`;
  }

  public distributeValues(): void {
    if (this.valueForm.get('estimationMethod')?.value) {
      this.alertService.clearAll();

      const consumption = this.valueForm.get('periodUsage')?.value;

      if (this.totalDays >= 1) {
        /** Equal Split **/
        if (this.valueForm.get('estimationMethod')?.value === 'ManualEvenly') {
          const hourlyValue = (consumption / this.totalDays) / 24;
          this.populateDistribution(hourlyValue);
        }
        /** Last Position **/
        if (this.valueForm.get('estimationMethod')?.value === 'ManualLastPosition') {
          const controls = Object.keys(this.intervalForm.controls);
          const lastKey = controls.at(-1);

          if (lastKey) {
            this.intervalForm.get(lastKey)?.setValue(
              this.valueForm.get('periodUsage')?.value / this.totalDays
            );
          }
        }
      } else {
        this.alertService.open('modal', 'danger', 'error.timeseries.daycount')
        console.error('Day Count is less than required.');
      }
    }
  }

  public populateDistribution(hourlyValue: number): void {
    const start = new Date(this.valueForm.get('start')?.value);
    const end = new Date(this.valueForm.get('end')?.value);
    const readingType = this.mapQuantityQualityToReadingType(this.form.get('quantityQuality')?.value);

    if (!start || !end || !readingType) {
      this.valueForm.markAllAsTouched();
      this.form.markAllAsTouched();
      console.error('Forms are not valid');
      return;
    }

    const newValues: { [key: string]: number } = {};
    const resolutionValue = this.form.get('resolution')?.value;

    let distributedValue = 0;
    if (resolutionValue === 'Hour') {
      distributedValue = hourlyValue;
    }
    else if (resolutionValue === 'Quarter')
    {
      distributedValue = hourlyValue / 4;
    }

    for (const array of this.intervalArray) {
      for (const element of array) {
        newValues[this.formatFormControlName(element)] = Number.parseFloat(distributedValue.toFixed(4));
      }
    }
    this.intervalForm.patchValue(newValues);

    this.form.get('meteredData')?.setValue(this.generateMeteredData());
  }

  public generateMeteredData(): { readingType: MeterStandType, readingTs: string, consumption: string }[] {
    const start = new Date(this.valueForm.get('start')?.value);
    const mappedReadingType = this.mapQuantityQualityToReadingType(this.form.get('quantityQuality')?.value);
    const resolutionValue = this.form.get('resolution')?.value;

    // Get template values (single day)
    const templateValues: number[] = [];
    for (const array of this.intervalArray) {
      for (const element of array) {
        templateValues.push(this.intervalForm.get(this.formatFormControlName(element))?.value ?? 0);
      }
    }

    if (resolutionValue === 'Hour') {
      return this.generateHourlyMeteredData(this.totalDays, start, mappedReadingType, templateValues);
    } else if (resolutionValue === 'Quarter') {
      return this.generateQuarterlyMeteredData(this.totalDays, start, mappedReadingType, templateValues);
    }
    return [];
  }

  public generateHourlyMeteredData(days: number, start: Date, readingType: any, templateHourlyValues: number[]): { readingType: MeterStandType, readingTs: string, consumption: string }[] {
    const result: { readingType: MeterStandType, readingTs: string, consumption: string }[] = [];

    // Apply template to each day
    for (let d = 0; d < days; d++) {
      for (let h = 0; h < 24; h++) {
        const date = new Date(start);
        date.setDate(start.getDate() + d);
        date.setHours(h, 0, 0, 0);

        result.push({
          readingType: readingType,
          readingTs: date.toISOString(),
          consumption: templateHourlyValues[h].toString() // Use template value
        });
      }
    }

    return result;
  }

  public generateQuarterlyMeteredData(days: number, start: Date, readingType: any, templateQuarterlyValues: number[]): { readingType: MeterStandType, readingTs: string, consumption: string }[] {
    const result: { readingType: MeterStandType, readingTs: string, consumption: string }[] = [];

    // Apply template to each day
    for (let d = 0; d < days; d++) {
      for (let h = 0; h < 24; h++) {
        for (let q = 0; q < 4; q++) {
          const date = new Date(start);
          date.setDate(start.getDate() + d);
          date.setHours(h, q * 15, 0, 0);

          result.push({
            readingType: readingType,
            readingTs: date.toISOString(),
            consumption: templateQuarterlyValues[h * 4 + q].toString() // Use template value
          });
        }
      }
    }

    return result;
  }

  public submit(): void {
    this.form.get('meteringPointIdentification')?.setValue(this.id);
    this.form.get('meteredData')?.setValue(this.generateMeteredData());
    this.form.get('estimationMethod')?.setValue(this.valueForm.get('estimationMethod')?.value);
    this.form.get('wantsDataLocked')?.setValue(JSON.parse(this.form.value.wantsDataLocked));

    // Manual Profile has it's own call
    if (this.form.get('estimationMethod')?.value === EstimationMethod.ManualProfile) {
      this.formApiService.submit(
        this.form,
        (data) => this.meteredDataService.meteredDataSubmitProfileMeteredDataPost(data),
        {
          preprocess: (data: any) => ({
            meteringPointIdentification: data.meteringPointIdentification,
            startDate: this.valueForm.get('start')?.value,
            endDate: this.valueForm.get('end')?.value,
            totalConsumption: this.valueForm.get('periodUsage')?.value,
          }),
          successMessage: 'meteringpoint.timeseries.manualsubmit.success',
          closeModal: true,
        }
      )
      return;
    }

    // Submit external profile metered data from other metering point
    if (this.form.get('estimationMethod')?.value === EstimationMethod.ManualOtherMeteringPoint) {
      this.formApiService.submit(
        this.form,
        (data: SubmitExternalProfileMeteredDataCommand) => this.meteredDataService.meteredDataSubmitExternalProfileMeteredDataPost(data),
        {
          preprocess: (data: any): SubmitExternalProfileMeteredDataCommand => ({
            meteringPointIdentification: data.meteringPointIdentification,
            profileMeteringPointIdentification: data.otherMeteringPoint,
            startDate: this.valueForm.get('start')?.value,
            endDate: this.valueForm.get('end')?.value,
            totalConsumption: this.valueForm.get('periodUsage')?.value,
          }),
          successMessage: 'meteringpoint.timeseries.manualsubmit.success',
          closeModal: true,
        }
      );

      return;
    }

    // Call for all other estimation methods
    this.formApiService.submit(
      this.form,
      (data) => this.meteredDataService.meteredDataSubmitManualMeteredDataPost(data),
      {
        successMessage: 'meteringpoint.timeseries.manualsubmit.success',
        closeModal: true,
      }
    )
  }

  /**
   * Returns true when both dates are filled but the range is less than one full day,
   * meaning the template table cannot be populated.
   */
  public get showDateRangeWarning(): boolean {
    const start = this.valueForm.get('start')?.value;
    const end = this.valueForm.get('end')?.value;
    return !!(start && end && this.totalDays < 1);
  }

  /**
   * Computes the live sum of all values in a given column index.
   * Reads directly from intervalForm controls so it stays reactive.
   *
   * @param columnIndex The 0-based index into each row's item array.
   */
  public getColumnSum(columnIndex: number): number {
    return this.intervalArray.reduce((sum, item) => {
      const value = this.intervalForm.get(this.formatFormControlName(item[columnIndex]))?.value ?? 0;
      return sum + Number(value);
    }, 0);
  }

  private mapQuantityQualityToReadingType(quantityQuality?: QuantityQuality | string | null): MeterStandType | null {
    if (quantityQuality == null) return null;

    const mapping: Record<string, MeterStandType> = {
      NotSpecified: MeterStandType.NotSpecified,
      Estimated: MeterStandType.Estimated,
      AsRead: MeterStandType.Ok
    };

    const key = String(quantityQuality);
    return mapping[key] ?? null;
  }

  protected readonly EstimationMethod = EstimationMethod;
}
